import { useState, useEffect, useRef } from "react";
import { X, Plus, Loader2, Check } from "lucide-react";
import axiosInstance from "../../API/interceptor";

export default function CategoryEditPopup({ taskId, currentCategories = [], onClose, onSaved }) {
  const [categories, setCategories] = useState([...currentCategories]);
  const [available,  setAvailable]  = useState([]);
  const [search,     setSearch]     = useState("");
  const [loading,    setLoading]    = useState(true);
  const [saving,     setSaving]     = useState(false);
  const [toast,      setToast]      = useState(null);
  const searchRef = useRef(null);

  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 2500);
  };


  useEffect(() => {
    axiosInstance.get("/task_assignment/get_functional_categories")
      .then(res => { const data = Array.isArray(res.data) ? res.data : []; setAvailable(data); })
      .catch(() => showToast("Failed to load categories", "error"))
      .finally(() => { setLoading(false); setTimeout(() => searchRef.current?.focus(), 100); });
  }, []);

  const addCategory    = (cat) => { if (!categories.includes(cat)) setCategories(prev => [...prev, cat]); };
  const removeCategory = (cat) => setCategories(prev => prev.filter(c => c !== cat));

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await axiosInstance.post(
        `/task_assignment/update_functional_categories/${taskId}`,
        { functional_categories: [...new Set(categories)] },
        { headers: { "Content-Type": "application/json" } }
      );
      if (res.data?.success || res.data?.message) {
        showToast("Categories updated!", "success");
        setTimeout(() => { onSaved([...new Set(categories)]); onClose(); }, 800);
      } else {
        showToast("Failed to update", "error");
      }
    } catch {
      showToast("Error saving categories", "error");
    } finally {
      setSaving(false);
    }
  };

  const filtered = available
    .filter(c => !categories.includes(c))
    .filter(c => c.toLowerCase().includes(search.toLowerCase()));

  return (
    <>
      {/* Backdrop */}
      <div onClick={onClose} className="fixed inset-0 bg-[rgba(15,23,42,0.3)] z-[2000]"/>

      {/* Popup */}
      <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[min(420px,95vw)] bg-white rounded-2xl shadow-[0_24px_60px_rgba(0,0,0,.18)] z-[2001] flex flex-col max-h-[85vh] overflow-hidden">

        {/* Header */}
        <div className="flex items-center justify-between px-[18px] py-[14px] border-b border-[#F1F5F9] shrink-0 bg-[linear-gradient(135deg,#2563EB,#1d4ed8)]">
          <div className="flex items-center gap-2">
            <div className="w-[30px] h-[30px] rounded-lg bg-white/20 flex items-center justify-center">
              <Plus size={15} color="#fff"/>
            </div>
            <p className="m-0 text-[14px] font-bold text-white">Edit Categories</p>
          </div>
          <button onClick={onClose}
            className="w-7 h-7 rounded-[6px] border-none bg-white/15 cursor-pointer flex items-center justify-center">
            <X size={13} color="#fff"/>
          </button>
        </div>

        {/* Current categories */}
        <div className="px-[18px] py-3 border-b border-[#F1F5F9] shrink-0">
          <p className="m-0 mb-2 text-[10px] font-bold text-[#94A3B8] uppercase tracking-[.08em]">Current</p>
          {categories.length === 0 ? (
            <p className="text-[12px] text-[#CBD5E1] m-0">No categories assigned</p>
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {categories.map(cat => (
                <span key={cat} className="inline-flex items-center gap-[5px] text-[11px] font-semibold px-2 py-[3px] rounded-full bg-[#EFF6FF] text-[#1d4ed8] border border-[#BFDBFE]">
                  {cat}
                  <button onClick={() => removeCategory(cat)}
                    className="border-none bg-transparent cursor-pointer p-0 leading-none text-[#93C5FD] flex items-center"
                    onMouseEnter={e => e.currentTarget.style.color = "#DC2626"}
                    onMouseLeave={e => e.currentTarget.style.color = "#93C5FD"}>
                    <X size={10}/>
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Search + list */}
        <div className="flex-1 overflow-hidden flex flex-col min-h-0">
          <div className="px-[18px] pt-[10px] pb-[6px] shrink-0">
            <p className="m-0 mb-1.5 text-[10px] font-bold text-[#94A3B8] uppercase tracking-[.08em]">Add Category</p>
            <input ref={searchRef} value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search categories..."
              className="w-full box-border border-[1.5px] border-[#E2E8F0] rounded-lg px-3 py-[7px] text-[12px] outline-none text-[#374151]"
              onFocus={e => e.target.style.borderColor = "#2563EB"}
              onBlur={e => e.target.style.borderColor = "#E2E8F0"}/>
          </div>

          <div className="flex-1 overflow-y-auto px-[18px] pb-3 flex flex-col gap-[3px]">
            {loading && (
              <div className="flex items-center gap-2 text-[#94A3B8] py-3 text-[12px]">
                <Loader2 size={14} className="animate-spin"/> Loading categories...
              </div>
            )}
            {!loading && filtered.length === 0 && (
              <p className="text-[12px] text-[#CBD5E1] my-2">{search ? "No results found" : "All categories already added"}</p>
            )}
            {!loading && filtered.map(cat => (
              <div key={cat} onClick={() => addCategory(cat)}
                className="flex items-center justify-between px-[10px] py-[7px] rounded-lg cursor-pointer text-[12px] text-[#374151] transition-colors duration-[120ms] border border-transparent"
                onMouseEnter={e => { e.currentTarget.style.background="#EFF6FF"; e.currentTarget.style.borderColor="#BFDBFE"; }}
                onMouseLeave={e => { e.currentTarget.style.background="transparent"; e.currentTarget.style.borderColor="transparent"; }}>
                <span>{cat}</span>
                <Plus size={13} color="#2563EB" className="shrink-0"/>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div className="px-[18px] py-3 border-t border-[#F1F5F9] flex gap-2 shrink-0">
          <button onClick={onClose}
            className="flex-1 py-2 rounded-lg border-[1.5px] border-[#E2E8F0] bg-white text-[12px] font-semibold text-[#64748B] cursor-pointer">
            Cancel
          </button>
          <button onClick={handleSave} disabled={saving}
            className={`flex-[2] py-2 rounded-lg border-none text-[12px] font-bold flex items-center justify-center gap-1.5 ${saving ? "bg-[#E2E8F0] text-[#94A3B8] cursor-not-allowed" : "bg-[linear-gradient(135deg,#2563EB,#1d4ed8)] text-white cursor-pointer shadow-[0_3px_10px_rgba(37,99,235,.3)]"}`}>
            {saving
              ? <><Loader2 size={12} className="animate-spin"/> Saving...</>
              : <><Check size={13}/> Save Changes</>}
          </button>
        </div>

        {/* Toast */}
        {toast && (
          <div className={`absolute bottom-[70px] left-1/2 -translate-x-1/2 text-white text-[11px] font-semibold px-4 py-[6px] rounded-full whitespace-nowrap shadow-[0_4px_12px_rgba(0,0,0,.15)] ${toast.type === "success" ? "bg-[#059669]" : "bg-[#DC2626]"}`}>
            {toast.type === "success" ? "✅" : "❌"} {toast.msg}
          </div>
        )}
      </div>

      <style>{`@keyframes cpSpin { to { transform: rotate(360deg); } }`}</style>
    </>
  );
}
